import { useEffect, useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Wallet, Plus, X, Pencil, Trash2, Lock, Settings2, ListChecks } from "lucide-react";
import {
  createExpenseSchema,
  updateExpenseSettingsSchema,
  type CreateExpenseInput,
  type UpdateExpenseSettingsInput,
} from "@finora/validation";
import type { ExpenseDto, TaxRateDto } from "@finora/types";
import { formatMoney } from "@finora/config";
import { ApiError } from "@finora/api-client";
import { Button, EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

type SupplierHit = { key: string; name: string; pib: string; hint: string };

/** Pretraga dobavljača dok se kuca (AJAX): prvo sopstveni dobavljači, a za PIB od 9 cifara i spoljni registar. */
function SupplierSearchInput({
  field,
  label,
  placeholder,
  value,
  onChange,
  onPick,
}: {
  field: "name" | "pib";
  label: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  onPick: (name: string, pib: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState<SupplierHit[]>([]);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  const skip = useRef(false);

  useEffect(() => {
    if (skip.current) {
      skip.current = false;
      return;
    }
    const term = value.trim();
    const digits = term.replace(/\D/g, "");
    const enough = field === "pib" ? digits.length >= 3 : term.length >= 2;
    if (!enough) {
      setHits([]);
      return;
    }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      setBusy(true);
      const found: SupplierHit[] = [];
      try {
        const local = await api.listSuppliers(field === "pib" ? digits : term);
        for (const s of local) found.push({ key: `s-${s.id}`, name: s.name, pib: s.taxId ?? "", hint: "Vaš dobavljač" });
        if (field === "pib" && digits.length === 9 && !local.some((s) => s.taxId === digits)) {
          try {
            const r = await api.lookupCompanyByPib(digits);
            if (r?.name) found.push({ key: `r-${digits}`, name: r.name, pib: r.pib || digits, hint: "Pronađeno u registru" });
          } catch {
            /* spoljni registar nedostupan - ostaju samo sopstveni dobavljači */
          }
        }
      } catch {
        /* pretraga je pomoć, ne sme da smeta unosu */
      }
      if (mine === seq.current) {
        setHits(found);
        setBusy(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value, field]);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <input
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
      />
      {open && (busy || hits.length > 0) && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-border bg-surface text-sm shadow-lg">
          {busy && hits.length === 0 && <li className="px-3 py-2 text-slate-400">Pretraga...</li>}
          {hits.map((h) => (
            <li key={h.key}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  skip.current = true;
                  onPick(h.name, h.pib);
                  setOpen(false);
                  setHits([]);
                }}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50"
              >
                <span className="truncate text-slate-800">{h.name}</span>
                <span className="shrink-0 text-xs text-slate-400">
                  {h.pib} · {h.hint}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const MODULE_KEY = "troskovi";

const SOURCE_LABEL: Record<string, string> = {
  MANUAL: "Ručni unos",
  MOBILE_QR: "Mobilni (QR)",
  SEF_INVOICE: "SEF faktura",
};

// Isti obrazac kao FiscalDocumentsListPage.tsx (toIsoDate, commit bf567ef) -
// NAMERNO ne koristi .toISOString() (UTC), već lokalne komponente datuma, da
// filter "Danas"/"Ovaj mesec" ne "zakasni" uveče po Europe/Belgrade vremenu.
function toIsoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Bruto (sa PDV-om) iznos stavke = količina * cena - samo za ŽIVI prikaz u
 * formi dok korisnik kuca (isti obrazac kao calculateItem() u
 * money.util.ts na backendu, koji radi KONAČAN, autoritativan obračun sa
 * zaokruživanjem - ovo je samo preview pre slanja).
 */
function rowGross(quantity: string, unitPriceGross: string): number {
  const q = Number(quantity) || 0;
  const p = Number(unitPriceGross) || 0;
  return q * p;
}

function rowNet(quantity: string, unitPriceGross: string, ratePercent: string): number {
  const gross = rowGross(quantity, unitPriceGross);
  const rate = Number(ratePercent) || 0;
  return gross / (1 + rate / 100);
}

type PeriodPreset = "today" | "this-month" | "custom";
type TabKey = "lista" | "podesavanja";

function ExpenseFormModal({
  expense,
  onClose,
}: {
  expense: ExpenseDto | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const { data: taxRates } = useQuery({
    queryKey: ["tax-rates"],
    queryFn: () => api.listTaxRates(),
  });
  const { data: companySettings } = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => api.getCompanySettings(),
  });
  const isVatRegistered = companySettings?.isVatRegistered ?? true;

  const defaultTaxRateId = (rates: TaxRateDto[] | undefined) =>
    rates?.find((r) => r.isDefault)?.id ?? rates?.[0]?.id ?? "";

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateExpenseInput>({
    resolver: zodResolver(createExpenseSchema),
    defaultValues: {
      date: expense?.date ?? toIsoDate(new Date()),
      supplierName: expense?.supplierName ?? "",
      supplierPib: expense?.supplierPib ?? "",
      description: expense?.description ?? "",
      items: expense
        ? expense.items.map((i) => ({
            name: i.name,
            quantity: i.quantity,
            unitPriceGross: i.unitPriceGross,
            // Ista logika kao SalesDocumentEditorPage - DTO ne vraća
            // taxRateId (samo denormalizovan taxRatePercent), pa se ID
            // rekonstruiše po procentu iz trenutne liste stopa firme.
            taxRateId: taxRates?.find((r) => r.ratePercent === i.taxRatePercent)?.id ?? "",
          }))
        : [{ name: "", quantity: "1", unitPriceGross: "", taxRateId: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items" });

  // Kad se stope učitaju (async), popuni podrazumevanu stopu na praznim
  // redovima (novi trošak - inicijalni red se kreira pre nego što stope
  // stignu sa servera).
  useEffect(() => {
    if (!taxRates || taxRates.length === 0) return;
    fields.forEach((field, idx) => {
      if (!field.taxRateId) {
        setValue(`items.${idx}.taxRateId`, defaultTaxRateId(taxRates));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxRates]);

  const items = watch("items");

  const save = useMutation({
    mutationFn: (data: CreateExpenseInput) =>
      expense ? api.updateExpense(expense.id, data) : api.createExpense(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      onClose();
    },
  });

  const totals = (items ?? []).reduce(
    (acc, item) => {
      const rate = taxRates?.find((r) => r.id === item.taxRateId)?.ratePercent ?? "0";
      const gross = rowGross(item.quantity, item.unitPriceGross);
      const net = rowNet(item.quantity, item.unitPriceGross, rate);
      return { net: acc.net + net, tax: acc.tax + (gross - net), gross: acc.gross + gross };
    },
    { net: 0, tax: 0, gross: 0 },
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <form
        onSubmit={handleSubmit((data) => save.mutate(data))}
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-auto rounded-xl bg-surface shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">
            {expense ? "Izmena troška" : "Novi trošak"}
          </h2>
          <button
            type="button"
            aria-label="Zatvori"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          {save.isError && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">
              {save.error instanceof ApiError ? save.error.response.message : "Čuvanje nije uspelo."}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Datum *</label>
              <input
                type="date"
                {...register("date")}
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
              {errors.date && <p className="mt-1 text-xs text-red-600">{errors.date.message}</p>}
            </div>
            <SupplierSearchInput
              field="name"
              label="Naziv dobavljača"
              value={watch("supplierName") ?? ""}
              onChange={(v) => setValue("supplierName", v, { shouldDirty: true })}
              onPick={(n, p) => {
                setValue("supplierName", n, { shouldDirty: true });
                if (p) setValue("supplierPib", p, { shouldDirty: true });
              }}
            />
          </div>

          <SupplierSearchInput
            field="pib"
            label="PIB dobavljača"
            value={watch("supplierPib") ?? ""}
            onChange={(v) => setValue("supplierPib", v, { shouldDirty: true })}
            onPick={(n, p) => {
              setValue("supplierPib", p, { shouldDirty: true });
              if (n) setValue("supplierName", n, { shouldDirty: true });
            }}
          />

          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-800">Stavke *</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500">
                  <th className="pb-1">Naziv stavke</th>
                  <th className="w-20 pb-1">Kol.</th>
                  <th className="w-28 pb-1">Cena</th>
                  {isVatRegistered && <th className="w-32 pb-1">PDV</th>}
                  <th className="w-24 pb-1 text-right">Ukupno</th>
                  <th className="w-8 pb-1" />
                </tr>
              </thead>
              <tbody>
                {fields.map((field, idx) => {
                  const item = items?.[idx];
                  const rate = taxRates?.find((r) => r.id === item?.taxRateId)?.ratePercent ?? "0";
                  const gross = rowGross(item?.quantity ?? "0", item?.unitPriceGross ?? "0");
                  return (
                    <tr key={field.id}>
                      <td className="py-1 pr-1">
                        <input
                          {...register(`items.${idx}.name` as const)}
                          placeholder="npr. Kancelarijski materijal"
                          className="h-9 w-full rounded-md border border-border px-2 text-sm focus:border-brand focus:outline-none"
                        />
                      </td>
                      <td className="py-1 pr-1">
                        <input
                          {...register(`items.${idx}.quantity` as const)}
                          inputMode="decimal"
                          className="h-9 w-full rounded-md border border-border px-2 text-sm focus:border-brand focus:outline-none"
                        />
                      </td>
                      <td className="py-1 pr-1">
                        <input
                          {...register(`items.${idx}.unitPriceGross` as const)}
                          inputMode="decimal"
                          placeholder="npr. 1234.50"
                          className="h-9 w-full rounded-md border border-border px-2 text-sm focus:border-brand focus:outline-none"
                        />
                      </td>
                      {isVatRegistered ? (
                        <td className="py-1 pr-1">
                          <select
                            {...register(`items.${idx}.taxRateId` as const)}
                            className="h-9 w-full rounded-md border border-border px-1 text-sm focus:border-brand focus:outline-none"
                          >
                            {taxRates?.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.ratePercent}%
                              </option>
                            ))}
                          </select>
                        </td>
                      ) : (
                        // Firma nije u sistemu PDV-a - polje se i dalje mora
                        // registrovati (backend uvek zahteva taxRateId, isti
                        // obrazac kao TaxRatesService.list - firma van PDV-a
                        // ima tačno jednu, oslobođenu 0% stopu), samo se ne
                        // prikazuje select jer nema izbora.
                        <input type="hidden" {...register(`items.${idx}.taxRateId` as const)} />
                      )}
                      <td className="py-1 pl-1 text-right font-medium text-slate-700">
                        {formatMoney(gross.toFixed(2))}
                      </td>
                      <td className="py-1 pl-1">
                        <button
                          type="button"
                          aria-label="Ukloni stavku"
                          onClick={() => fields.length > 1 && remove(idx)}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-30"
                          disabled={fields.length <= 1}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {errors.items?.message && (
              <p className="mt-1 text-xs text-red-600">{errors.items.message as string}</p>
            )}
            <button
              type="button"
              onClick={() =>
                append({ name: "", quantity: "1", unitPriceGross: "", taxRateId: defaultTaxRateId(taxRates) })
              }
              className="mt-2 flex items-center gap-1 text-sm font-medium text-brand hover:underline"
            >
              <Plus className="h-4 w-4" aria-hidden />
              Dodaj stavku
            </button>

            <div className="mt-3 flex justify-end">
              <div className="w-56 space-y-1 text-sm">
                {isVatRegistered && (
                  <>
                    <div className="flex justify-between text-slate-500">
                      <span>Osnovica</span>
                      <span>{formatMoney(totals.net.toFixed(2))}</span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span>PDV</span>
                      <span>{formatMoney(totals.tax.toFixed(2))}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between border-t border-border pt-1 text-base font-semibold text-slate-900">
                  <span>Ukupno</span>
                  <span>{formatMoney(totals.gross.toFixed(2))}</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Napomena</label>
            <textarea
              rows={2}
              {...register("description")}
              placeholder="Opciona slobodna napomena uz trošak"
              className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Otkaži
          </Button>
          <Button type="submit" disabled={isSubmitting || save.isPending}>
            {save.isPending ? "Čuvam..." : "Sačuvaj"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function ExpensesListTab() {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<PeriodPreset>("this-month");
  const [customFrom, setCustomFrom] = useState(toIsoDate(new Date()));
  const [customTo, setCustomTo] = useState(toIsoDate(new Date()));
  const [editing, setEditing] = useState<ExpenseDto | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: companySettings } = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => api.getCompanySettings(),
  });
  const isVatRegistered = companySettings?.isVatRegistered ?? true;

  const range =
    period === "today"
      ? { from: toIsoDate(new Date()), to: toIsoDate(new Date()) }
      : period === "this-month"
        ? {
            from: toIsoDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
            to: toIsoDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)),
          }
        : { from: customFrom, to: customTo };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["expenses", range.from, range.to],
    queryFn: () => api.listExpenses({ from: range.from, to: range.to }),
    retry: false,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteExpense(id),
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
    },
    onError: (err) => {
      setDeleteError(err instanceof ApiError ? err.response.message : "Brisanje nije uspelo.");
    },
  });

  if (isError) {
    if (error instanceof ApiError && error.response.errorCode === "MODULE_NOT_AVAILABLE") {
      return <ModuleGateNotice error={error} moduleKey={MODULE_KEY} />;
    }
    return (
      <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
        {error instanceof Error ? error.message : "Učitavanje nije uspelo."}
      </p>
    );
  }

  const totalGross = (data ?? []).reduce((sum, e) => sum + Number(e.amount), 0);
  const totalTax = (data ?? []).reduce((sum, e) => sum + Number(e.taxAmount), 0);

  return (
    <div className="space-y-4">
      {deleteError && (
        <div className="flex items-start justify-between rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="ml-3 text-red-400 hover:text-red-600">
            ×
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <div className="flex gap-1">
          {(
            [
              { key: "today", label: "Danas" },
              { key: "this-month", label: "Ovog meseca" },
              { key: "custom", label: "Od - do" },
            ] as { key: PeriodPreset; label: string }[]
          ).map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`rounded-lg px-3 py-2 text-sm font-medium ${
                period === p.key ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-end gap-2">
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
            <span className="text-slate-400">—</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
        )}
        <Button className="ml-auto" onClick={() => setShowNew(true)}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          Novi trošak
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <div className="p-5">
            <EmptyState icon={Wallet} title="Nema troškova za izabrani period" />
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Datum</th>
                <th className="px-4 py-3">Dobavljač</th>
                <th className="px-4 py-3">PIB</th>
                <th className="px-4 py-3">Stavke</th>
                <th className="px-4 py-3">Izvor</th>
                {isVatRegistered && <th className="px-4 py-3 text-right">Osnovica</th>}
                {isVatRegistered && <th className="px-4 py-3 text-right">PDV</th>}
                <th className="px-4 py-3 text-right">Ukupno</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.map((e) => (
                <tr key={e.id} className="border-b border-border last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-600">
                    {new Date(e.date).toLocaleDateString("sr-RS")}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800">{e.supplierName ?? "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{e.supplierPib ?? "—"}</td>
                  <td className="px-4 py-3 text-slate-500">
                    {e.items.length === 1
                      ? (e.items[0]?.name ?? "")
                      : `${e.items.length} stavke`}
                    {e.description && (
                      <div className="text-xs text-slate-400">{e.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge label={SOURCE_LABEL[e.source] ?? e.source} tone="neutral" />
                  </td>
                  {isVatRegistered && (
                    <td className="px-4 py-3 text-right text-slate-500">{formatMoney(e.netAmount)}</td>
                  )}
                  {isVatRegistered && (
                    <td className="px-4 py-3 text-right text-slate-500">{formatMoney(e.taxAmount)}</td>
                  )}
                  <td className="px-4 py-3 text-right font-medium text-slate-800">
                    {formatMoney(e.amount)}
                  </td>
                  <td className="px-4 py-3">
                    {e.reportedAt ? (
                      <StatusBadge label="Poslato u izveštaju" tone="success" />
                    ) : (
                      <StatusBadge label="Nije poslato" tone="warning" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {e.reportedAt ? (
                        <span
                          className="text-slate-300"
                          title="Već poslato u izveštaju - trošak se više ne može menjati ni brisati"
                        >
                          <Lock className="h-4 w-4" aria-hidden />
                        </span>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditing(e)}
                            className="text-slate-400 hover:text-brand"
                            title="Izmeni"
                          >
                            <Pencil className="h-4 w-4" aria-hidden />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm("Obrisati ovaj trošak?")) {
                                remove.mutate(e.id);
                              }
                            }}
                            disabled={remove.isPending}
                            className="text-slate-400 hover:text-red-600"
                            title="Obriši"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} className="px-4 py-3 text-right font-medium text-slate-600">
                  Ukupno
                </td>
                <td />
                {isVatRegistered && (
                  <td className="px-4 py-3 text-right font-medium text-slate-600">
                    {formatMoney((totalGross - totalTax).toFixed(2))}
                  </td>
                )}
                {isVatRegistered && (
                  <td className="px-4 py-3 text-right font-medium text-slate-600">
                    {formatMoney(totalTax.toFixed(2))}
                  </td>
                )}
                <td className="px-4 py-3 text-right font-semibold text-slate-900">
                  {formatMoney(totalGross.toFixed(2))}
                </td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {(showNew || editing) && (
        <ExpenseFormModal
          expense={editing}
          onClose={() => {
            setShowNew(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ExpenseSettingsTab() {
  const queryClient = useQueryClient();
  const {
    data: settings,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["expense-settings"],
    queryFn: () => api.getExpenseSettings(),
    retry: false,
  });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<UpdateExpenseSettingsInput>({
    resolver: zodResolver(updateExpenseSettingsSchema),
    defaultValues: { enabled: false, destination: "ACCOUNTANT_EMAIL", accountantEmail: "" },
  });

  useEffect(() => {
    if (settings) {
      reset({
        enabled: settings.enabled,
        destination: settings.destination,
        accountantEmail: settings.accountantEmail ?? "",
      });
    }
  }, [settings, reset]);

  const destination = watch("destination");

  const save = useMutation({
    mutationFn: (data: UpdateExpenseSettingsInput) => api.updateExpenseSettings(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(["expense-settings"], updated);
    },
  });

  if (isError) {
    if (error instanceof ApiError && error.response.errorCode === "MODULE_NOT_AVAILABLE") {
      return <ModuleGateNotice error={error} moduleKey={MODULE_KEY} />;
    }
    return (
      <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
        {error instanceof Error ? error.message : "Učitavanje nije uspelo."}
      </p>
    );
  }

  if (isLoading || !settings) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  return (
    <form
      onSubmit={handleSubmit((data) => save.mutate(data))}
      className="max-w-xl space-y-5 rounded-xl border border-border bg-surface p-5 shadow-subtle"
    >
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Dnevni izveštaj troškova</h2>
        <p className="text-xs text-slate-400">
          Ako je uključeno, jednom dnevno se šalju svi troškovi od prethodnog dana - na email
          knjigovođe ili u MiniMax, zavisno od izabranog odredišta. Dan bez troškova se preskače.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" {...register("enabled")} className="h-4 w-4 rounded border-border text-brand focus:ring-brand" />
        Uključi dnevni izveštaj troškova
      </label>

      <div>
        <label className="block text-xs font-medium text-slate-500">Odredište izveštaja</label>
        <select
          {...register("destination")}
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        >
          <option value="ACCOUNTANT_EMAIL">Knjigovođa (email)</option>
          <option value="MINIMAX">MiniMax (uskoro - trenutno neaktivno)</option>
        </select>
        {destination === "MINIMAX" && !settings.minimaxAvailable && (
          <p className="mt-1 text-xs text-amber-600">
            Slanje u MiniMax još nije aktivno - podešavanje se može sačuvati, ali izveštaj se za
            sada neće poslati dok ova integracija ne bude gotova.
          </p>
        )}
      </div>

      {destination === "ACCOUNTANT_EMAIL" && (
        <div>
          <label className="block text-xs font-medium text-slate-500">Email knjigovođe *</label>
          <input
            type="email"
            {...register("accountantEmail")}
            placeholder="knjigovodja@primer.rs"
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
          {errors.accountantEmail && (
            <p className="mt-1 text-xs text-red-600">{errors.accountantEmail.message}</p>
          )}
        </div>
      )}

      {save.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {save.error instanceof ApiError ? save.error.response.message : "Čuvanje nije uspelo."}
        </p>
      )}
      {save.isSuccess && !save.isPending && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Sačuvano.</p>
      )}

      {settings.lastSentAt && (
        <p className="text-xs text-slate-400">
          Poslednji izveštaj poslat: {new Date(settings.lastSentAt).toLocaleString("sr-RS")}
        </p>
      )}

      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? "Čuvam..." : "Sačuvaj podešavanja"}
      </Button>
    </form>
  );
}

export function ExpensesPage() {
  const [tab, setTab] = useState<TabKey>("lista");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Troškovi</h1>
        <p className="text-sm text-slate-500">
          Evidencija troškova firme (ručni unos i skenirani fiskalni računi iz mobilne aplikacije)
          i podešavanje dnevnog izveštaja knjigovođi.
        </p>
      </div>

      <div className="flex gap-1 border-b border-border">
        <button
          onClick={() => setTab("lista")}
          className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${
            tab === "lista" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <ListChecks className="h-4 w-4" aria-hidden />
          Troškovi
        </button>
        <button
          onClick={() => setTab("podesavanja")}
          className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${
            tab === "podesavanja" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <Settings2 className="h-4 w-4" aria-hidden />
          Podešavanja izveštaja
        </button>
      </div>

      {tab === "lista" ? <ExpensesListTab /> : <ExpenseSettingsTab />}
    </div>
  );
}
